/**
 *
 * SideBar
 *
 */

import React, { FunctionComponent, memo } from 'react';
import { Wrap } from '../../assets/styledAssets/grid';
import {
  CertificateIcon,
  CloseIcon,
  DocIcon,
  GraphIcon,
  GroupIcon,
  HomeIcon,
  ImportedResults,
  LogoSm,
  MedalIcon,
  PinLocationIcon,
  SettingsIcon,
  TestGroups,
  Trophy,
  UserIcon,
  UsersIcon,
} from '../Icons/Icons.component';
import {
  accentColorPrim,
  accentColorTertiary,
  fontColorSecond,
  red,
} from '../../assets/styledAssets/vars';
import {ImgWrap, StyledNavLink, StyledSideBar} from './styles';
import { Account, AccountType, Roles, User } from '../../domains/app';
import { useIntl } from 'react-intl';
import messages from './messages';
import { avatarLink } from '../../helpers/app.helpers';
import { Menu } from '@material-ui/core';
import { authProviderDefault } from '../../providers/auth-provider';
import { StyledMenuItem } from '../Button/styles';
import Button from '../Button/Button.component';
import { NavLink } from 'react-router-dom';

interface IProps {
  user: User | null;
  selectedAccount: Account | undefined;
  className?: string;
  onBarClose?: any;
  isSidebarShow: boolean;
}

const SideBar: FunctionComponent<IProps> = ({
  user,
  selectedAccount,
  className,
  onBarClose,
  isSidebarShow,
}) => {
  const intl = useIntl();

  const hasRights = (roles: Roles[]) => {
    return roles.some((r) => (selectedAccount?.role as number) & r);
  };

  function handleLogout() {
    authProviderDefault.logout();
  }

  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  // function handleNavigateLink(param) {
  //   ()=><Redirect to={param}/>
  // }

  return (
    <StyledSideBar isSidebarShow={isSidebarShow}>
      <Wrap>
        <Wrap
          width={'100%'}
          margin={'0 3rem 2rem 3rem'}
          alignItems={'center'}
          justifyCont={'space-between'}
        >
          <LogoSm color={accentColorPrim} width={'7rem'} />
          <Wrap hiddenSM>
            <Button icon transparent onClick={onBarClose}>
              <CloseIcon color={fontColorSecond} width={'2rem'} />
            </Button>
          </Wrap>
        </Wrap>
        <NavLink
          style={{ textDecoration: 'none', flexGrow: 1 }}
          onClick={onBarClose}
          to={'/select-account'}
        >
          <Wrap
            margin={['1rem 3rem 0', '5rem 3rem']}
            br={'1.2rem'}
            bgColor={accentColorTertiary}
            flexDir={'column'}
            padding={'2rem'}
            style={{ cursor: 'pointer' }}
          >
            <Wrap
              margin={'0 0 1rem 0'}
              fs={'2.4rem'}
              fw={'bold'}
              color={'#000'}
            >
              {selectedAccount?.name}
            </Wrap>
            <Wrap fs={'1.4rem'} color={fontColorSecond}>
              {selectedAccount?.type === AccountType.SPACE
                ? intl.formatMessage(messages.space)
                : intl.formatMessage(messages.account)}
            </Wrap>
          </Wrap>
        </NavLink>
        <Wrap width={'100%'} margin={'2rem 0 0 0'} flexDir={'column'}>
          {hasRights([Roles.Administrator, Roles.SiteOwner]) && (
            <StyledNavLink
              onClick={onBarClose}
              to={'/users'}
              activeClassName={'active'}
            >
              <UsersIcon width={'2.5rem'} />
              <span>{intl.formatMessage(messages.users)}</span>
            </StyledNavLink>
          )}
          {hasRights([Roles.SpaceOwner, Roles.SpaceAdministrator]) && (
            <StyledNavLink
              onClick={onBarClose}
              to={'/members'}
              activeClassName={'active'}
            >
              <UsersIcon width={'2.5rem'} />
              <span>{intl.formatMessage(messages.members)}</span>
            </StyledNavLink>
          )}
          {hasRights([Roles.SpaceOwner, Roles.SpaceAdministrator]) && (
            <StyledNavLink
              onClick={onBarClose}
              exact
              to={'/groups'}
              activeClassName={'active'}
            >
              <GroupIcon width={'2.5rem'} />
              <span>{intl.formatMessage(messages.groups)}</span>
            </StyledNavLink>
          )}
          <Wrap width={'100%'} hiddenXS>
            {hasRights([Roles.Administrator, Roles.SiteOwner]) && (
              <StyledNavLink
                onClick={onBarClose}
                to={'/questionnaires'}
                activeClassName={'active'}
              >
                <DocIcon width={'2.5rem'} />
                <span>{intl.formatMessage(messages.questionnaires)}</span>
              </StyledNavLink>
            )}
          </Wrap>
          {hasRights([Roles.Administrator, Roles.SiteOwner]) && (
            <StyledNavLink
              onClick={onBarClose}
              exact
              to={'/spaces'}
              activeClassName={'active'}
            >
              <PinLocationIcon width={'2.5rem'} />
              <span>{intl.formatMessage(messages.spaces)}</span>
            </StyledNavLink>
          )}
          {hasRights([Roles.User]) && (
            <StyledNavLink
              onClick={onBarClose}
              to={'/home'}
              activeClassName={'active'}
            >
              <HomeIcon width={'2.5rem'} />
              <span>{intl.formatMessage(messages.home)}</span>
            </StyledNavLink>
          )}
          {hasRights([
            Roles.User,
            Roles.SpaceOwner,
            Roles.SpaceAdministrator,
            Roles.Administrator,
            Roles.SiteOwner,
          ]) && (
            <StyledNavLink
              onClick={onBarClose}
              exact
              to={'/test-results'}
              activeClassName={'active'}
            >
              <GraphIcon width={'2.5rem'} />
              <span>{intl.formatMessage(messages.testResults)}</span>
            </StyledNavLink>
          )}
          {hasRights([Roles.User, Roles.Administrator, Roles.SiteOwner]) && (
            <StyledNavLink
              onClick={onBarClose}
              exact
              to={'/qualifications'}
              activeClassName={'active'}
            >
              <CertificateIcon width={'2.5rem'} />
              <span>{intl.formatMessage(messages.qualifications)}</span>
            </StyledNavLink>
          )}
          {hasRights([Roles.User, Roles.Administrator, Roles.SiteOwner]) && (
            <StyledNavLink
              onClick={onBarClose}
              exact
              to={'/achievements'}
              activeClassName={'active'}
            >
              <MedalIcon width={'2.5rem'} />
              <span>{intl.formatMessage(messages.achievements)}</span>
            </StyledNavLink>
          )}
          {hasRights([Roles.User]) && (
            <StyledNavLink
              onClick={onBarClose}
              exact
              to={'/profile'}
              activeClassName={'active'}
            >
              <UserIcon width={'2.5rem'} />
              <span>{intl.formatMessage(messages.profile)}</span>
            </StyledNavLink>
          )}
          {hasRights([Roles.SpaceOwner]) && (
            <StyledNavLink
              onClick={onBarClose}
              exact
              to={'/settings'}
              activeClassName={'active'}
            >
              <SettingsIcon width={'2.5rem'} />
              <span>{intl.formatMessage(messages.settings)}</span>
            </StyledNavLink>
          )}
          {hasRights([Roles.SiteOwner]) && (
            <StyledNavLink
              onClick={onBarClose}
              exact
              to={'/owner-test-results'}
              activeClassName={'active'}
            >
              <ImportedResults width={'2.5rem'} />
              <span>{intl.formatMessage(messages.testResultsImported)}</span>
            </StyledNavLink>
          )}
          {hasRights([Roles.SiteOwner]) && (
            <StyledNavLink
              onClick={onBarClose}
              exact
              to={'/test-types'}
              activeClassName={'active'}
            >
              <TestGroups width={'2.5rem'} />
              <span>{intl.formatMessage(messages.testType)}</span>
            </StyledNavLink>
          )}
          {hasRights([Roles.SiteOwner, Roles.Administrator]) && (
            <StyledNavLink
              onClick={onBarClose}
              exact
              to={'/world-records'}
              activeClassName={'active'}
            >
              <Trophy width={'2.5rem'} />
              <span>{intl.formatMessage(messages.worldRecords)}</span>
            </StyledNavLink>
          )}
        </Wrap>
      </Wrap>
      <Wrap width={'100%'} padding={'0 3rem 0 3rem'} alignItems={'center'}>
        <Button
          onClick={handleClick}
          aria-controls="simple-menu"
          aria-haspopup="true"
        >
          <ImgWrap pic={avatarLink(user?.avatarId as string)} />
          <Wrap
            flexDir={'column'}
            maxWidth={'calc(100% - 8rem)'}
            margin={'0 0 0 2rem'}
          >
            <Wrap
              fs={'2.4rem'}
              fw={'bold'}
              color={'#000'}
              margin={'0 0 .5rem 0'}
              textLeft
            >
              {user?.firstName} {user?.lastName}
            </Wrap>
            <Wrap fs={'1.4rem'} color={fontColorSecond}>
              Sport LTD
            </Wrap>
          </Wrap>
        </Button>
        <Menu
          id="simple-menu"
          anchorEl={anchorEl}
          keepMounted
          anchorOrigin={{
            vertical: 'top',
            horizontal: 'left',
          }}
          transformOrigin={{
            vertical: 'bottom',
            horizontal: 'left',
          }}
          open={Boolean(anchorEl)}
          onClose={handleClose}
        >
          {/* <StyledMenuItem onClick={handleClose}><Wrap fs={'1.4rem'} fw={'bold'} padding={'.5rem 1rem'}>{intl.formatMessage(messages.helpPage)}</Wrap></StyledMenuItem>
          <StyledMenuItem onClick={handleClose}><Wrap fs={'1.4rem'} fw={'bold'} color={accentColorPrim} padding={'.5rem 1rem'}>{intl.formatMessage(messages.support)}</Wrap></StyledMenuItem>*/}
          <StyledMenuItem onClick={handleLogout}>
            <Wrap fs={'1.4rem'} fw={'bold'} color={red} padding={'.5rem 1rem'}>
              {intl.formatMessage(messages.logout)}
            </Wrap>
          </StyledMenuItem>
        </Menu>
      </Wrap>
    </StyledSideBar>
  );
};

export default memo(SideBar);
